import { StoreType } from '../../_enums/stores/store-type.enum';
import { AmazonStoreType } from '../../_enums/seller-channels/amazon.enums';

// Numeric UI enums. Normalize API enum names at the service boundary.

export enum MessageThreadStatus {
  NeedsResponse = 0,
  AwaitingBuyer = 1,
  Resolved = 2,
  ReportedUnresolved = 3,
  Spam = 4,
  Trash = 5
}

export enum MessageDirection {
  Inbound = 0,
  Outbound = 1
}

export enum MessageAuthorType {
  Buyer = 0,
  Human = 1,
  Ai = 2,
  AiHoldingReply = 3
}

export enum MessageAiDraftStatus {
  Pending = 0,
  Approved = 1,
  Edited = 2,
  Rejected = 3,
  AutoSent = 4,
  Discarded = 5
}

export enum MessageCategory {
  Unknown = 0,
  WhereIsMyOrder = 1,
  InvoiceRequest = 2,
  ProductQuestion = 3,
  ReturnOrRefund = 4,
  Cancellation = 5,
  DamagedOrDefective = 6,
  WrongItemReceived = 7,
  Feedback = 8,
  Escalation = 9
}

export enum MessageAutoSendMode {
  Off = 0,
  DraftOnly = 1,
  AutoSendAboveConfidence = 2
}

export enum MessageChannel {
  Email = 0,
  Phone = 1
}

/** A reply is sent to the buyer; a return request is only approved or rejected, never sent. */
export enum MessageAiDraftKind {
  Reply = 0,
  ReturnRequest = 1
}

export enum PhoneCallOutcome {
  InProgress = 0,
  ResolvedByAi = 1,
  Transferred = 2,
  MessageTaken = 3,
  Abandoned = 4,
  Failed = 5
}

export const PHONE_CALL_OUTCOME_LABELS: { [key: number]: string } = {
  [PhoneCallOutcome.InProgress]: 'In progress',
  [PhoneCallOutcome.ResolvedByAi]: 'Resolved by AI',
  [PhoneCallOutcome.Transferred]: 'Transferred',
  [PhoneCallOutcome.MessageTaken]: 'Message taken',
  [PhoneCallOutcome.Abandoned]: 'Caller hung up',
  [PhoneCallOutcome.Failed]: 'Agent unavailable'
};

export const MESSAGE_CATEGORY_LABELS: { [key: number]: string } = {
  [MessageCategory.Unknown]: 'Unrecognised',
  [MessageCategory.WhereIsMyOrder]: 'Where is my order',
  [MessageCategory.InvoiceRequest]: 'Invoice request',
  [MessageCategory.ProductQuestion]: 'Product question',
  [MessageCategory.ReturnOrRefund]: 'Return or refund',
  [MessageCategory.Cancellation]: 'Cancellation',
  [MessageCategory.DamagedOrDefective]: 'Damaged or defective',
  [MessageCategory.WrongItemReceived]: 'Wrong item received',
  [MessageCategory.Feedback]: 'Feedback',
  [MessageCategory.Escalation]: 'Escalation'
};

export interface PendingDraftSummary {
  id: number;
  kind?: MessageAiDraftKind;
  confidence: number;
  category: MessageCategory;
  autoSendBlockedReason: string;
}

export interface MessageThreadListItem {
  id: number;
  storeType: number;
  channel?: MessageChannel;
  callOutcome?: PhoneCallOutcome;
  callDurationSeconds?: number;
  subject: string;
  buyerName: string;
  marketplaceOrderNumber: string;
  orderId?: number;
  status: MessageThreadStatus;
  category: MessageCategory;
  lastMessageAt: string;
  lastMessageInbound: boolean;
  preview: string;
  pendingDraft?: PendingDraftSummary;
  /** Hours the buyer has been waiting; null once answered. */
  hoursWaiting?: number;
  isOverdue: boolean;
}

export interface MessageFolderCounts {
  all: number;
  needsResponse: number;
  awaitingBuyer: number;
  resolved: number;
  reportedUnresolved: number;
  spam: number;
  trash: number;
  calls?: number;
}

export interface MessageMetrics {
  needsResponse: number;
  overdue: number;
  resolvedToday: number;
  awaitingApproval: number;
  slaHours: number;
}

export interface MessageThreadListResponse {
  threads: MessageThreadListItem[];
  folders: MessageFolderCounts;
  metrics: MessageMetrics;
}

export interface MarketplaceMessageDto {
  fromAddress?: string;
  toAddress?: string;
  id: number;
  direction: MessageDirection;
  authorType: MessageAuthorType;
  authorUserId: string;
  body: string;
  attachmentsJson: string;
  sentAt: string;
  messageAiDraftId?: number;
}

export interface MessageAiDraftDto {
  id: number;
  body: string;
  provider: number;
  model: string;
  category: MessageCategory;
  confidence: number;
  reasoning: string;
  /** Why the policy would not let this send itself. Null when it may. */
  autoSendBlockedReason: string;
  createdDate: string;
  alwaysManual?: boolean;
}

export interface MessageOrderItemContext {
  sku: string;
  title: string;
  quantity: number;
  unitPrice?: number;
  trackingNumber: string;
  status: string;
}

export interface MessageOrderContext {
  storeType?: number;
  amazonStoreType?: number;
  marketplaceOrderId?: string;
  purchaseOrderNumber?: string;
  storeName: string;
  orderId: number;
  orderNumber: string;
  orderDate: string;
  currencyCode: string;
  orderTotal?: number;
  fulfillmentChannel: string;
  shipCity: string;
  shipCountry: string;
  earliestDeliveryDate?: string;
  latestDeliveryDate?: string;
  items: MessageOrderItemContext[];
}

export interface EmailOrderCandidate {
  orderId: number; orderNumber: string; storeName: string; orderDate: string; orderTotal?: number; currencyCode: string;
}

/** The call behind a Phone thread. The recording itself is fetched separately, through the API. */
export interface MessageCallDto {
  fromNumber: string;
  toNumber: string;
  startedAt: string;
  endedAt?: string;
  durationSeconds?: number;
  outcome: PhoneCallOutcome;
  summary?: string;
  transferredTo?: string;
  hasRecording: boolean;
}

export interface PendingReturnRequest {
  id: number;
  body: string;
  createdDate: string;
}

export interface MessageThreadDetail {
  channel?: MessageChannel;
  call?: MessageCallDto;
  pendingReturnRequests?: PendingReturnRequest[];
  emailOrderCandidates?: EmailOrderCandidate[];
  replyToAddress?: string;
  replyFromAddress?: string;
  replyFromName?: string;
  replyIdentityError?: string;
  id: number;
  storeType: number;
  storeId?: number;
  subject: string;
  buyerName: string;
  marketplaceOrderNumber: string;
  orderId?: number;
  status: MessageThreadStatus;
  category: MessageCategory;
  lastMessageAt: string;
  awaitingResponseSince?: string;
  firstRespondedAt?: string;
  resolvedAt?: string;
  assignedToUserId: string;
  messages: MarketplaceMessageDto[];
  pendingDraft?: MessageAiDraftDto;
  order?: MessageOrderContext;
  canReply: boolean;
}

export interface ReplyAttachment {
  fileName: string;
  contentBase64: string;
}

export interface SendReplyRequest {
  attachments?: ReplyAttachment[];
  body: string;
  draftId?: number;
  resolve?: boolean;
}

export interface GenerateDraftRequest {
  instruction?: string;
  provider?: number;
}

export interface ResponseLogRow {
  id: number;
  messageThreadId: number;
  sentAt: string;
  authorType: MessageAuthorType;
  authorUserId: string;
  storeType: number;
  buyer: string;
  subject: string;
  orderNumber: string;
  threadStatus: MessageThreadStatus;
  category: MessageCategory;
  confidence?: number;
  model: string;
  provider?: number;
  draftStatus?: MessageAiDraftStatus;
  respondedAfterHours?: number;
  excerpt: string;
}

export interface ResponseLogSummary {
  total: number;
  byHuman: number;
  byAi: number;
  holdingReplies: number;
  approvedVerbatim: number;
  editedBeforeSending: number;
  autoSent: number;
  /** Share of reviewed drafts sent without a word changed. Null until anything has been reviewed. */
  verbatimRate?: number;
}

export interface ResponseLogResponse {
  rows: ResponseLogRow[];
  total: number;
  summary: ResponseLogSummary;
}

export interface MessagingSettings {
  storeReplySenders?: { [storeId: number]: { emailAddress: string; displayName: string } };
  autoSendMode: MessageAutoSendMode;
  minConfidenceToAutoSend: number;
  autoSendCategories: MessageCategory[];
  sendHoldingReply: boolean;
  holdingReplyDelayMinutes: number;
  holdingReplyText: string;
  signatureText: string;
  replyGuidance: string;
  topicGuidance?: { [category: number]: string };
  inboundAddress: string;
  replyFromAddress: string;
  replyFromName: string;
  selectableCategories: MessageCategory[];
  alwaysManualCategories: MessageCategory[];
  hasAiKey: boolean;
}

export interface PhoneAgentHours {
  open: string;
  close: string;
}

export interface PhoneAgentSettings {
  enabled: boolean;
  twilioAccountSid?: string;
  /** Only whether a token is saved; the token itself never comes back from the API. */
  hasTwilioAuthToken?: boolean;
  /** Write-only. Left blank to keep the saved token. */
  twilioAuthToken?: string;
  twilioNumber?: string;
  twilioConnectedAt?: string;
  greeting: string;
  voice: string;
  businessHours: { [day: string]: PhoneAgentHours };
  timeZone: string;
  transferNumber?: string;
  maxCallMinutes: number;
  returnPolicy?: string;
  voices?: string[];
  hasOpenAiKey?: boolean;
}

export interface SimulateInboundRequest {
  threadKey?: string;
  buyerName?: string;
  subject?: string;
  body: string;
  orderNumber?: string;
  storeType?: number;
}

export enum MailboxProvider {
  Gmail = 0,
  MicrosoftGraph = 1
}

export enum MailboxAuthMode {
  OAuthRefreshToken = 0,
  ServiceAccountImpersonation = 1
}

export interface MailboxConnection {
  id: number;
  provider: MailboxProvider;
  authMode: MailboxAuthMode;
  emailAddress: string;
  displayName: string;
  replyIdentities?: { emailAddress: string; displayName: string }[];
  defaultStoreId?: number;
  importHistoryDays?: number;
  ingestQuery: string;
  enabled: boolean;
  lastPolledAt?: string;
  lastSuccessAt?: string;
  /** Why this mailbox stopped working, so it does not just look quiet. */
  lastError?: string;
  hasCredentials: boolean;
}

export interface MailboxConnectionsResponse {
  connections: MailboxConnection[];
  defaultIngestQuery: string;
  stores: { id: number; name: string; type: number }[];
}

export interface SaveMailboxRequest {
  id?: number;
  provider: MailboxProvider;
  authMode: MailboxAuthMode;
  emailAddress: string;
  displayName?: string;
  replyIdentities?: { emailAddress: string; displayName: string }[];
  defaultStoreId?: number;
  importHistoryDays?: number;
  /** Omitted when editing, to keep the stored credentials. */
  credentials?: string;
  ingestQuery?: string;
  enabled: boolean;
}

/** Accept the API's named enums as well as numeric responses, without changing user text. */
export function normalizeMessageHubResponse<T>(response: T): T {
  const enums: { [field: string]: object } = {
    storeType: StoreType, amazonStoreType: AmazonStoreType,
    autoSendMode: MessageAutoSendMode, authMode: MailboxAuthMode,
    provider: MailboxProvider, category: MessageCategory, direction: MessageDirection,
    authorType: MessageAuthorType,
    status: { ...MessageThreadStatus, ...MessageAiDraftStatus },
    autoSendCategories: MessageCategory, selectableCategories: MessageCategory,
    alwaysManualCategories: MessageCategory,
    channel: MessageChannel, kind: MessageAiDraftKind,
    outcome: PhoneCallOutcome, callOutcome: PhoneCallOutcome
  };
  const visit = (value: any, field = ''): any => {
    if (Array.isArray(value)) { return value.map(item => visit(item, field)); }
    if (value !== null && typeof value === 'object') {
      return Object.keys(value).reduce((result, key) => ({ ...result, [key]: visit(value[key], key) }), {});
    }
    if (typeof value === 'string' && enums[field]) {
      const entry = Object.keys(enums[field]).find(key => key.toLowerCase() === value.toLowerCase()
        && typeof enums[field][key] === 'number');
      if (entry) { return enums[field][entry]; }
      if (/^\d+$/.test(value)) { return Number(value); }
    }
    return value;
  };
  return visit(response);
}
